/**
 * 인증 관련 API 서비스
 * - 로그인/로그아웃 처리
 * - 회원가입 처리
 * - 토큰 관리
 * - 파일 업로드
 * - 중복 계정 체크
 */
import { apiClient } from '@/features/auth/lib/authClient';
import { removeEmptyFields } from '@/utils/util';
import {
  FileUploadResponse,
  MemberSignUpFormData,
  RefreshTokenResponse,
  SignInResponse,
  SignUpFormData,
  SignupMetaResponse,
} from '../types/api/auth';
import {
  AuthProvider,
  AvailabilityCheckType,
  FileUploadType,
} from '../types/enum';

export const authService = {
  /**
   * 파일 업로드
   * @param file - 업로드할 파일
   * @param type - 파일 타입 (사업자등록증, 통장사본, 명함)
   * @returns 업로드된 파일 URL
   */
  uploadFile: async (
    file: File,
    type: FileUploadType,
  ): Promise<FileUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await apiClient.post<FileUploadResponse>(
      '/files/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  },

  /**
   * 사업자 중복 체크
   * @param businessNumber - 사업자 번호
   */
  checkDuplicateCompany: async (businessNumber: string): Promise<void> => {
    return (
      await apiClient.post('/auth/check-duplicate', {
        type: AvailabilityCheckType.COMPANY,
        value: businessNumber.replace(/-/g, ''),
      })
    ).data;
  },

  /**
   * 이용자 ID 중복 체크
   * @param provider - 소셜 로그인 제공자
   * @param signinId - 소셜 계정 ID
   */
  checkDuplicateUser: async (
    provider: AuthProvider,
    signinId: string,
  ): Promise<void> => {
    return (
      await apiClient.post('/auth/check-duplicate', {
        type: AvailabilityCheckType.USER,
        method: provider,
        value: signinId,
      })
    ).data;
  },

  /** 회원가입 메타 정보 */
  getSignupMeta: async (): Promise<SignupMetaResponse> => {
    const response = await apiClient.get<SignupMetaResponse>(
      '/auth/signup:metadata',
    );
    return response.data;
  },

  /**
   * 회원가입
   * @param data - 회원가입 데이터
   * @returns 회원가입 응답 데이터
   */
  signUp: async (
    data: MemberSignUpFormData | SignUpFormData,
  ): Promise<SignInResponse> => {
    const cleanedData = removeEmptyFields(data);
    const response = await apiClient.post<SignInResponse>(
      '/auth/signup',
      cleanedData,
    );
    return response.data;
  },

  /**
   * 이메일 로그인
   * @param signin_id - 로그인 ID
   * @param password - 비밀번호
   * @returns 로그인 응답 데이터
   */
  signInWithEmail: async (
    signin_id: string,
    password: string,
  ): Promise<SignInResponse> => {
    const response = await apiClient.post<SignInResponse>('/auth/signin', {
      method: AuthProvider.EMAIL,
      signin_id,
      password: password,
    });
    return response.data;
  },

  /**
   * 소셜 로그인
   * @param provider - 소셜 로그인 제공자
   * @param signinId - 소셜 계정 ID
   * @param accessToken - 소셜 로그인 토큰
   * @returns 로그인 응답 데이터
   */
  signInWithSocial: async (
    provider: AuthProvider,
    signinId: string,
    accessToken?: string,
  ): Promise<SignInResponse> => {
    const response = await apiClient.post<SignInResponse>('/auth/signin', {
      method: provider,
      signin_id: signinId,
      access_token: accessToken,
    });

    return response.data;
  },

  /**
   * 로그아웃
   * @param refreshToken - 리프레시 토큰
   */
  signOut: async (refreshToken: string): Promise<void> => {
    return (
      await apiClient.post('/auth/signout', {
        refresh: refreshToken,
      })
    ).data;
  },

  /**
   * 토큰 갱신
   * @param refreshToken - 리프레시 토큰
   * @returns 새로운 액세스 토큰
   */
  refreshToken: async (refreshToken: string): Promise<RefreshTokenResponse> => {
    const response = await apiClient.post<RefreshTokenResponse>(
      '/auth/refresh',
      {
        refresh: refreshToken,
      },
    );
    return response.data;
  },

  /**
   * 회원 탈퇴
   * @param accessToken - 액세스 토큰
   */
  deleteAccount: async (accessToken: string): Promise<void> => {
    return await apiClient.delete('/auth/unregister', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  },
};
